import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { BarChart3Icon, FilterIcon, UserIcon, ListIcon, PieChartIcon } from "lucide-react";
import { DashboardHeader } from "../../components/DashboardHeader";
import { AlertCircle } from "lucide-react";

// Import components
import FeedbackStats from "@/components/feedback-reports/FeedbackStats";
import FeedbackFilters from "@/components/feedback-reports/FeedbackFilters";
import FeedbackCharts from "@/components/feedback-reports/FeedbackCharts";
import FeedbackDetailCard from "@/components/feedback-reports/FeedbackDetailCard";

interface FeedbackItem {
  id: string;
  course_id?: string;
  user: {
    name: string;
    position: string;
    school: string;
  };
  course: {
    title: string;
    category: string;
    id?: string;
  };
  rating: number;
  feedback: string;
  date: string;
  recommendRating?: number;
}

const FeedbackReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("charts");
  const [feedbackData, setFeedbackData] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [presenters, setPresenters] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    dateFrom: undefined,
    dateTo: undefined,
    presenter: "",
    minRating: "",
  });

  useEffect(() => {
    fetchFeedbackData();
    fetchCategories();
    fetchPresenters();
  }, [filters]);

  const fetchFeedbackData = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("course_registrations")
        .select(
          `
          id,
          course_id,
          overall_rating,
          feedback,
          recommend_rating,
          registration_date,
          courses (
            id,
            title,
            category
          ),
          profiles (
            first_name,
            last_name,
            position,
            school
          )
        `
        )
        .not("feedback", "is", null);

      if (filters.search) {
        query = query.ilike("courses.title", `%${filters.search}%`);
      }

      if (filters.category) {
        query = query.eq("courses.category", filters.category);
      }

      if (filters.presenter) {
        query = query.eq("courses.presenter", filters.presenter);
      }

      if (filters.minRating) {
        query = query.gte("overall_rating", parseInt(filters.minRating));
      }

      if (filters.dateFrom) {
        query = query.gte("registration_date", filters.dateFrom.toISOString().split('T')[0]);
      }

      if (filters.dateTo) {
        query = query.lte("registration_date", filters.dateTo.toISOString().split('T')[0]);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching feedback:", error);
      } else {
        const formattedData: FeedbackItem[] = data.map((item: any) => ({
          id: item.id,
          course_id: item.course_id,
          user: {
            name: `${item.profiles?.first_name || ""} ${item.profiles?.last_name || ""}`,
            position: item.profiles?.position || "Unknown",
            school: item.profiles?.school || "Unknown",
          },
          course: {
            id: item.courses?.id,
            title: item.courses?.title || "Unknown",
            category: item.courses?.category || "Uncategorized",
          },
          rating: item.overall_rating || 0,
          feedback: item.feedback || "No feedback provided",
          date: item.registration_date || "Unknown",
          recommendRating: item.recommend_rating,
        }));
        setFeedbackData(formattedData);
      }
    } catch (error) {
      console.error("Failed to fetch feedback data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("courses")
        .select("category");

      if (error) {
        console.error("Error fetching categories:", error);
      } else {
        const uniqueCategories = Array.from(new Set(data.map((item: any) => item.category)));
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchPresenters = async () => {
    try {
      const { data, error } = await supabase
        .from("courses")
        .select("presenter")
        .not("presenter", "is", null);

      if (error) {
        console.error("Error fetching presenters:", error);
      } else {
        const uniquePresenters = Array.from(new Set(data.map((item: any) => item.presenter)));
        setPresenters(uniquePresenters);
      }
    } catch (error) {
      console.error("Failed to fetch presenters:", error);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const totalCoursesWithFeedback = new Set(feedbackData.map((item) => item.course.title)).size;
  const totalFeedbackSubmissions = feedbackData.length;
  const avgOverallRating =
    feedbackData.reduce((sum, item) => sum + item.rating, 0) / totalFeedbackSubmissions || 0;
  const recommendRate =
    feedbackData.filter((item) => item.recommendRating && item.recommendRating >= 3).length /
    totalFeedbackSubmissions || 0;

  const categoryData = categories.map((category) => ({
    name: category,
    count: feedbackData.filter((item) => item.course.category === category).length,
  }));

  const ratingDistribution = [...Array(4)].map((_, i) => ({
    rating: i + 1,
    count: feedbackData.filter((item) => item.rating === i + 1).length,
  }));

  const monthlyData = () => {
    const monthlyCounts: { [key: string]: { count: number; totalRating: number } } = {};

    feedbackData.forEach((item) => {
      const month = new Date(item.date).toLocaleString("default", { month: "short", year: '2-digit' });
      if (!monthlyCounts[month]) {
        monthlyCounts[month] = { count: 0, totalRating: 0 };
      }
      monthlyCounts[month].count++;
      monthlyCounts[month].totalRating += item.rating;
    });

    return Object.entries(monthlyCounts).map(([month, data]) => ({
      month: month,
      count: data.count,
      avgRating: data.totalRating / data.count,
    }));
  };

  return (
    <div className="w-full py-4 px-2 space-y-4">
      <DashboardHeader
        title="Feedback Reports"
        subtitle="View and analyze user feedback and course evaluations."
      />
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight font-title text-primary">
            Feedback Reports
          </h1>
          <p className="text-muted-foreground font-subtitle">
            Detailed insights into course feedback and participant experiences
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate("/admin/feedback/analytics")}
          className="border-accent hover:bg-accent hover:text-white"
        >
          <BarChart3Icon className="h-4 w-4 mr-2" />
          Advanced Analytics
        </Button>
      </div>

      <FeedbackStats
        totalCourses={totalCoursesWithFeedback}
        totalFeedback={totalFeedbackSubmissions}
        avgRating={avgOverallRating}
        recommendRate={recommendRate}
      />

      <FeedbackFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        categories={categories}
        presenters={presenters}
      />

      <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-accent-subtle border border-accent-light">
          <TabsTrigger 
            value="charts" 
            className="font-medium data-[state=active]:bg-accent data-[state=active]:text-white"
          >
            <PieChartIcon className="h-4 w-4 mr-2" />
            Charts
          </TabsTrigger>
          <TabsTrigger 
            value="details" 
            className="font-medium data-[state=active]:bg-accent data-[state=active]:text-white"
          >
            <ListIcon className="h-4 w-4 mr-2" />
            Details
          </TabsTrigger>
        </TabsList>
        <Separator className="bg-accent-light" />
        <TabsContent value="charts" className="space-y-4">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <p className="text-primary font-body">Loading charts...</p>
            </div>
          ) : (
            <FeedbackCharts
              categoryData={categoryData}
              ratingDistribution={ratingDistribution}
              monthlyData={monthlyData()}
            />
          )}
        </TabsContent>
        <TabsContent value="details" className="space-y-4">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <p className="text-primary font-body">Loading feedback details...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {feedbackData.map((feedback) => (
                <FeedbackDetailCard key={feedback.id} feedback={feedback} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

export default FeedbackReports;
